import { action, makeObservable, observable } from "mobx";
import CategoryAPI from "../api/CategoryAPI";
import {
  CategoryCreateUpdatePayload,
  CategoryDetail,
  CategoryListItem,
  CategoryOptionMapping,
  CategoryOptionMappingInput,
  GetCategoriesParams,
  PutCategoryOptionsBody,
} from "../../../models/Categories/types";

export class CategoryStore {
  categoryAPI: CategoryAPI;
  categories: CategoryListItem[] = [];
  categoryDetail: CategoryDetail | null = null;
  categoryOptions: CategoryOptionMapping[] = [];
  limit = 0;
  total_pages = 0;
  total_count = 0;
  error: string | null = null;

  constructor() {
    this.categoryAPI = new CategoryAPI();
    makeObservable(this, {
      categories: observable,
      categoryDetail: observable,
      categoryOptions: observable,
      limit: observable,
      total_pages: observable,
      total_count: observable,
      error: observable,
      clearCategoryDetail: action,
      listCategories: action,
      getCategoryById: action,
      createCategoryRecord: action,
      updateCategoryRecord: action,
      getCategoryOptions: action,
      putCategoryOptions: action,
      toggleCategoryActive: action,
      toggleCategoryDisplay: action,
      deleteCategoryRecord: action,
      patchListItem: action,
    });
  }

  clearCategoryDetail = () => {
    this.categoryDetail = null;
    this.categoryOptions = [];
  };

  patchListItem = (id: number, patch: Partial<CategoryListItem>) => {
    const idx = this.categories.findIndex((c) => c.id === id);
    if (idx >= 0) {
      this.categories[idx] = { ...this.categories[idx], ...patch };
    }
  };

  listCategories = async (params: GetCategoriesParams) => {
    try {
      const response = await this.categoryAPI.listCategories(params);
      this.limit = response.limit;
      this.total_pages = response.total_pages;
      this.total_count = response.total_count;
      this.categories = response.list;
      this.error = null;
      return response;
    } catch (error: any) {
      this.error =
        error?.response?.data?.message ||
        "Something went wrong loading categories.";
      throw error;
    }
  };

  getCategoryById = async (id: number) => {
    try {
      const category = await this.categoryAPI.getCategoryById(id);
      this.categoryDetail = category;
      return category;
    } catch (error: any) {
      throw error;
    }
  };

  createCategoryRecord = async (body: CategoryCreateUpdatePayload) => {
    try {
      const created = await this.categoryAPI.createCategory(body);
      this.categoryDetail = created;
      return created;
    } catch (error: any) {
      throw error;
    }
  };

  updateCategoryRecord = async (
    id: number,
    body: CategoryCreateUpdatePayload,
  ) => {
    try {
      const updated = await this.categoryAPI.updateCategory(id, body);
      this.categoryDetail = updated;
      this.patchListItem(id, updated);
      return updated;
    } catch (error: any) {
      throw error;
    }
  };

  getCategoryOptions = async (id: number) => {
    try {
      const options = await this.categoryAPI.getCategoryOptions(id);
      this.categoryOptions = options;
      return options;
    } catch (error: any) {
      throw error;
    }
  };

  putCategoryOptions = async (
    id: number,
    body: PutCategoryOptionsBody | CategoryOptionMappingInput[],
  ) => {
    try {
      const options = await this.categoryAPI.putCategoryOptions(id, body);
      this.categoryOptions = options;
      return options;
    } catch (error: any) {
      throw error;
    }
  };

  toggleCategoryActive = async (id: number, active: number) => {
    try {
      const result = await this.categoryAPI.toggleCategoryActive(id, active);
      this.patchListItem(id, { active: result.active });
      if (this.categoryDetail?.id === id) {
        this.categoryDetail = { ...this.categoryDetail, active: result.active };
      }
      return result;
    } catch (error: any) {
      throw error;
    }
  };

  toggleCategoryDisplay = async (id: number, display: number) => {
    try {
      const result = await this.categoryAPI.toggleCategoryDisplay(id, display);
      this.patchListItem(id, { display: result.display });
      if (this.categoryDetail?.id === id) {
        this.categoryDetail = {
          ...this.categoryDetail,
          display: result.display,
        };
      }
      return result;
    } catch (error: any) {
      throw error;
    }
  };

  deleteCategoryRecord = async (id: number) => {
    try {
      const result = await this.categoryAPI.deleteCategory(id);
      this.patchListItem(id, { active: 0 });
      if (this.categoryDetail?.id === id) {
        this.categoryDetail = { ...this.categoryDetail, active: 0 };
      }
      return result;
    } catch (error: any) {
      throw error;
    }
  };
}
